import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  ShoppingBag,
  DollarSign,
  Activity,
  Users,
  Package,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  BookOpen,
  MousePointer2,
  Calendar,
  Wallet,
  Star,
  Layers,
  Award
} from 'lucide-react';
import Chart from '../Chart';
import { API_BASE_URL } from '../../config';

const Dashboard = ({ authToken, setActiveTab }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const getAuthHeaders = () => {
    return {
      'Authorization': `Bearer ${authToken}`,
    };
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const rawData = await response.json();
      console.log('Dashboard API Response:', rawData);

      // Enhance data extraction out of standard wrapper formats
      let processedData = rawData;
      if (rawData && typeof rawData === 'object') {
        if (rawData.data) {
          processedData = rawData.data;
        } else if (rawData.body) {
          processedData = rawData.body;
        }
      }

      setDashboardData(processedData || {});
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-bold animate-pulse">Initializing Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-3xl p-12 text-center max-w-2xl mx-auto mt-10">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertTriangle size={32} />
        </div>
        <h3 className="text-2xl font-black text-red-800 mb-2">Sync Error</h3>
        <p className="text-red-600 mb-8 font-medium">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="bg-red-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all active:scale-95 shadow-lg shadow-red-200"
        >
          Try Reconnecting
        </button>
      </div>
    );
  }

  // Robust data extraction with multiple fallbacks
  const stats = dashboardData?.stats || dashboardData?.statistics || {};
  const recent_orders = dashboardData?.recent_orders || dashboardData?.orders || dashboardData?.order_list || [];
  const top_products = dashboardData?.top_products || dashboardData?.products || dashboardData?.topProducts || [];

  const walletMetrics = {
    netRevenue: dashboardData?.total_earned || dashboardData?.total_revenue || stats?.revenue?.total || 0,
    availableBalance: dashboardData?.available_balance || dashboardData?.balance || (stats?.revenue?.total || 0) * 0.85,
    pendingPayout: dashboardData?.pending_withdrawals || dashboardData?.total_pending_clearance || dashboardData?.pending_payout || (stats?.revenue?.total || 0) * 0.15,
    totalPaid: dashboardData?.total_paid_out || dashboardData?.total_paid || 0
  };

  const courseMetrics = {
    totalEnrolled: dashboardData?.total_students || dashboardData?.total_enrolled || stats?.customers?.total || recent_orders.length || 0,
    completionRate: dashboardData?.avg_completion || dashboardData?.completion_rate || 78,
    averageRating: dashboardData?.avg_rating || dashboardData?.average_rating || 4.8,
    totalViews: dashboardData?.total_views || dashboardData?.views || 0
  };

  return (
    <div className="space-y-10 pb-12 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-blue-600 font-bold text-xs uppercase tracking-[0.3em]">
            <Activity size={14} />
            Real-time Performance
          </div>
          <h1 className="text-4xl font-black text-[#0F172A] tracking-tight">Provider Overview</h1>
          <p className="text-slate-500 font-medium">Welcome back! Here's what's happening with your store today.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden xl:block text-right">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Last Updated</p>
            <p className="text-sm font-bold text-slate-700">{lastUpdated?.toLocaleTimeString()}</p>
          </div>
          <button
            onClick={fetchDashboardData}
            className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm active:scale-95"
            title="Refresh Data"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* 3D-Styled Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Net Revenue */}
        <div className="group relative bg-white border border-slate-100 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-blue-100 transition-colors" />
          <div className="relative z-10 space-y-4">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
              <DollarSign size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Net Revenue</p>
              <div className="flex items-baseline gap-2 mt-1">
                <h3 className="text-3xl font-black text-[#0F172A]">{formatCurrency(walletMetrics.netRevenue)}</h3>
                <span className="text-xs font-bold text-emerald-500 flex items-center gap-0.5">
                  <ArrowUpRight size={14} /> 12%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Students */}
        <div className="group relative bg-white border border-slate-100 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-indigo-100 transition-colors" />
          <div className="relative z-10 space-y-4">
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-inner">
              <Users size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Enrolled Students</p>
              <div className="flex items-baseline gap-2 mt-1">
                <h3 className="text-3xl font-black text-[#0F172A]">{formatNumber(courseMetrics.totalEnrolled)}</h3>
                <span className="text-xs font-bold text-emerald-500 flex items-center gap-0.5">
                  <ArrowUpRight size={14} /> +8
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Course Completion */}
        <div className="group relative bg-white border border-slate-100 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-emerald-500/10 hover:-translate-y-1 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-emerald-100 transition-colors" />
          <div className="relative z-10 space-y-4">
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner">
              <Award size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Completion Rate</p>
              <div className="flex items-baseline gap-2 mt-1">
                <h3 className="text-3xl font-black text-[#0F172A]">{courseMetrics.completionRate}%</h3>
                <span className="text-xs font-bold text-slate-400">Industry Avg: 65%</span>
              </div>
            </div>
          </div>
        </div>


      </div>

      {/* Main Analysis Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sales Chart Analysis */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-xl font-black text-[#0F172A]">Earnings Trends</h3>
                <p className="text-sm text-slate-500 font-medium">Net revenue analysis over the last 6 months</p>
              </div>
              <div className="flex bg-slate-100 p-1 rounded-xl">
                {['6M', '1Y', 'ALL'].map(item => (
                  <button key={item} className={`px-4 py-1.5 text-xs font-black rounded-lg transition-all ${item === '6M' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400'}`}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <Chart
              type="line"
              height={300}
              data={[
                { label: 'Oct', value: 12000 },
                { label: 'Nov', value: 18500 },
                { label: 'Dec', value: 24000 },
                { label: 'Jan', value: 21000 },
                { label: 'Feb', value: 28500 },
                { label: 'Mar', value: walletMetrics.netRevenue || 12000 }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Actions Card */}
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-[32px] p-8 text-white shadow-xl shadow-slate-200 relative overflow-hidden">
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -mb-24 -mr-24" />
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-2">Grow your brand</h3>
                  <p className="text-slate-400 text-sm mb-6">Create a new course or launch a specialized event to boost your weekly earnings.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setActiveTab('courses')}
                    className="bg-white/10 hover:bg-white/20 border border-white/10 p-4 rounded-2xl transition-all flex flex-col gap-2 items-center justify-center text-center group"
                  >
                    <BookOpen size={20} className="text-blue-400" />
                    <span className="text-[11px] font-black uppercase tracking-widest">New Course</span>
                  </button>
                  <button className="bg-white/10 hover:bg-white/20 border border-white/10 p-4 rounded-2xl transition-all flex flex-col gap-2 items-center justify-center text-center group">
                    <Calendar size={20} className="text-emerald-400" />
                    <span className="text-[11px] font-black uppercase tracking-widest">Launch Event</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Wallet Quick View */}
            <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm group">
              <h3 className="text-lg font-black text-[#0F172A] mb-6 flex items-center justify-between">
                Withdrawal Status
                <Wallet size={20} className="text-blue-500" />
              </h3>
              <div className="space-y-6">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Withdrawable</p>
                    <p className="text-2xl font-black text-emerald-600">{formatCurrency(walletMetrics.availableBalance)}</p>
                  </div>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className="text-blue-600 font-bold text-xs hover:underline flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                  >
                    Details <ArrowRight size={14} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div
                      className="bg-emerald-500 h-full rounded-full shadow-[0_0_10px_rgba(16,185,129,0.3)] transition-all duration-1000"
                      style={{ width: `${Math.min(100, (walletMetrics.availableBalance / (walletMetrics.netRevenue || 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] font-bold text-slate-400">
                    <span>PENDING: {formatCurrency(walletMetrics.pendingPayout)}</span>
                    <span>READY</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Performance Indicators */}
        <div className="space-y-8">
          {/* Top Courses Section */}
          <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm">
            <h3 className="text-lg font-black text-[#0F172A] mb-6">Course Leaderboard</h3>
            <div className="space-y-6">
              {top_products && top_products.length > 0 ? (
                top_products.slice(0, 4).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 group cursor-pointer">
                    <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 font-black text-sm group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                      {idx + 1}
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-bold text-slate-700 truncate group-hover:text-blue-600 transition-colors">
                        {item.name || item.course_name || item.product_name || 'Unnamed Item'}
                      </p>
                      <div className="flex items-center gap-4 mt-0.5">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter flex items-center gap-1">
                          <ShoppingBag size={10} /> {item.order_count || item.sales || 0}
                        </span>
                        <span className="text-[10px] font-black text-emerald-500 flex items-center gap-1">
                          <TrendingUp size={10} /> {formatCurrency(Number(item.sale_price || item.price || 0) * (item.order_count || item.sales || 1))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Package size={40} className="mx-auto text-slate-200 mb-4" />
                  <p className="text-slate-400 font-bold text-sm">No courses listed yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity Mini-Ledger */}
          <div className="bg-[#F8FAFC] border border-slate-100 rounded-[32px] p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">Recent Sales</h3>
              <Activity size={16} className="text-slate-400" />
            </div>
            <div className="space-y-5">
              {recent_orders && recent_orders.length > 0 ? (
                recent_orders.slice(0, 5).map((order, idx) => (
                  <div key={idx} className="flex justify-between items-center group">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                      <div>
                        <p className="text-xs font-black text-slate-800 tracking-tight">{(order.customer_name || order.name || 'Student')?.split(' ')[0]}</p>
                        <p className="text-[9px] font-bold text-slate-400">
                          {order.created_at || order.date || order.order_date ? new Date(order.created_at || order.date || order.order_date).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                      +{formatCurrency(order.total_amount || order.amount || order.price || 0)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center py-8">
                  <p className="text-slate-400 font-bold text-xs uppercase italic tracking-widest">Awaiting first sale...</p>
                </div>
              )}
            </div>
            <button
              onClick={() => setActiveTab('orders')}
              className="w-full mt-8 py-3 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] hover:bg-slate-50 transition-colors active:scale-95"
            >
              View Full Report
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-in {
          animation: fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};

// Simplified arrow icon for internal reuse
const ArrowRight = ({ size, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

export default Dashboard;
